<script setup lang="ts" generic="T extends FetchTicketResponse">
import { ref, triggerRef, onMounted, nextTick, watch, computed, onBeforeUnmount } from "vue";
import { faker } from "@faker-js/faker";
import { vInfiniteScroll } from "@vueuse/components";
import { useDebounceFn, useElementSize, useLocalStorage, useScroll } from "@vueuse/core";
import { addDays, format, getISOWeek, parseISO, startOfISOWeek } from "date-fns";
import { storeToRefs } from "pinia";
import { TicketFiltersDTOMatchMode, type FetchTicketResponse, type TicketFiltersDTO } from "src/api/models";
import {
  postFetchAllTickets,
  usePostFetchAllUnscheduledTickets,
  usePostFetchOverdueScheduledTickets,
} from "src/api/queries/order-portal-bff";
import { useTrelloColumns } from "src/composables/useTrelloColumns";
import { useTicketService } from "src/services/ticket-service";
import { useTrelloBoardStore } from "src/stores/trello-board-store";
import type { Column } from "src/types/trello-board-type";
import { formatWeekRange, parseUTCDate, toLocalDateString } from "src/utils/date-utc";

import { useTicketBoardSearch } from "src/composables/useTicketBoardSearch";
import { TicketEditDialog } from "../ticket-edit";
import QuickAssignUserDrawer from "./QuickAssignUserDrawer.vue";
import TrelloBoardColumnTab from "./TrelloBoardColumnTab.vue";
import TrelloBoardSearchPanel from "./TrelloBoardSearchPanel.vue";
import TrelloColumn from "./TrelloColumn.vue";
import TrelloContextMenu from "./TrelloContextMenu.vue";
import TrelloToolbar from "./TrelloToolbar.vue";

const emits = defineEmits<{
  "click-item": [val: T];
}>();

const store = useTrelloBoardStore();
const { fetchTicket, fetchTickets } = useTicketService();
const {
  mutateAsync: fetchUnscheduledTickets,
  data: unscheduledTickets,
  isPending: isFetchingUnscheduledTickets,
} = usePostFetchAllUnscheduledTickets();
const {
  mutateAsync: fetchOverdueScheduledTickets,
  data: overdueScheduledTickets,
  isPending: isFetchingOverdueScheduledTickets,
} = usePostFetchOverdueScheduledTickets();
const {
  searchInput,
  selectedStatusSort,
  selectedPrioritySort,
  selectedAssignees,
  selectedUnits,
  startDate,
  ticketContainerWidth,
  isWeekendVisible,
  singleLaneScroll,
} = storeToRefs(store);

const isQuickAssignUserDrawerVisible = ref(false);
const isGenerating = ref(false);
const noMoreFilteredResults = ref(false);
let generationId = 0;
const draggingTicket = ref(false);
const cutTicket = ref<FetchTicketResponse | null>(null);
const isTrelloBoardViewItemDialogVisible = ref(false);
const selectedTicket = ref<FetchTicketResponse | null>(null);
const infiniteScrollElement = ref<HTMLElement | null>(null);
const contextMenuTicket = ref<FetchTicketResponse | null>(null);
const contextMenuColumn = ref<Column<FetchTicketResponse> | null>(null);
const contextMenu = ref<InstanceType<typeof TrelloContextMenu> | null>(null);
const isUnresolvedExpanded = useLocalStorage("trello-unresolved-expanded", true);
const isUnplannedExpanded = useLocalStorage("trello-unplanned-expanded", true);
const isPendingExpanded = useLocalStorage("trello-pending-expanded", true);
const pinnedWorkloadColumnId = ref<string | null>(null);
const unresolvedTicketsData = ref<FetchTicketResponse[]>([]);
const unscheduledTicketsData = ref<FetchTicketResponse[]>([]);
const pendingTicketsData = ref<FetchTicketResponse[]>([]);
const isFetchingPendingTickets = ref(false);

const {
  isSearchActive,
  isSearching,
  isLoadingMore,
  searchError,
  hasMorePages,
  filteredUnresolved,
  filteredUnplanned,
  filteredPending,
  filteredScheduled,
  loadMoreResults,
} = useTicketBoardSearch(searchInput, unresolvedTicketsData, unscheduledTicketsData, pendingTicketsData);

const hasActiveFilters = computed(() => {
  return (
    (searchInput.value?.trim().length ?? 0) > 0 ||
    (selectedAssignees.value?.length ?? 0) > 0 ||
    (selectedStatusSort.value?.length ?? 0) > 0 ||
    (selectedUnits.value?.length ?? 0) > 0
  );
});
const daysPerScroll = computed(() => hasActiveFilters.value ? 30 : 7);

const { columns, loadingColumns, lastFetchedDate, visibleColumns, getDayName, updateWorkload } =
  useTrelloColumns(isWeekendVisible, hasActiveFilters);
const firstDayColumnId = computed(() => visibleColumns.value.find((c) => !c.separator)?.id ?? null);
const { x } = useScroll(infiniteScrollElement);
const { height: boardHeight } = useElementSize(infiniteScrollElement);

function isSameDay(d1: Date, d2: Date) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

function formatWeekRange(date: Date): string {
  const weekStart = startOfISOWeek(date);
  const weekEnd = addDays(weekStart, 6);
  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${format(weekStart, 'd')} – ${format(weekEnd, 'd MMM')}`;
  }
  return `${format(weekStart, 'd MMM')} – ${format(weekEnd, 'd MMM')}`;
}

function initializeStartDate() {
  lastFetchedDate.value = new Date(startDate.value?.getTime() || new Date().getTime());
}

function buildTicketFilters(startDate: string, endDate: string): TicketFiltersDTO {
  const searchText = searchInput.value?.trim() || "";
  const hasSearch = searchText.length > 0;

  const filters: TicketFiltersDTO = {
    match_mode: hasSearch ? TicketFiltersDTOMatchMode.or : TicketFiltersDTOMatchMode.and,
    start_date: startDate,
    end_date: endDate,
  };

  if (hasSearch) {
    filters.title = searchText;
    filters.description = searchText;
    const parsed = parseInt(searchText, 10);
    if (!isNaN(parsed)) {
      filters.number = parsed;
    }
  }

  if (selectedAssignees.value && selectedAssignees.value.length > 0) {
    filters.assignee_id = selectedAssignees.value[0];
  }

  if (selectedUnits.value && selectedUnits.value.length > 0) {
    filters.unit_id = selectedUnits.value[0];
  }

  if (selectedStatusSort.value && selectedStatusSort.value.length > 0 && selectedStatusSort.value[0] && store.ticketStatuses.length > 0) {
    const selectedStatus = store.ticketStatuses.find(
      (status) => status.name === selectedStatusSort.value[0]
    );
    if (selectedStatus) {
      filters.status_id = selectedStatus.id;
    }
  }

  return filters;
}

function hasJumpableFilter(): boolean {
  return (
    (selectedAssignees.value?.length ?? 0) > 0
    || (selectedUnits.value?.length ?? 0) > 0
    || (selectedStatusSort.value?.length ?? 0) > 0
  );
}

async function probeNextFilteredTicketDate(startFromDateStr: string): Promise<string | null> {
  if (!hasJumpableFilter()) return null;

  const probeFilters: TicketFiltersDTO = {
    match_mode: TicketFiltersDTOMatchMode.and,
    start_date: startFromDateStr,
  };
  if ((selectedAssignees.value?.length ?? 0) > 0) {
    probeFilters.assignee_id = selectedAssignees.value[0];
  }
  if ((selectedUnits.value?.length ?? 0) > 0) {
    probeFilters.unit_id = selectedUnits.value[0];
  }
  if (selectedStatusSort.value && selectedStatusSort.value.length > 0 && selectedStatusSort.value[0] && store.ticketStatuses.length > 0) {
    const selectedStatus = store.ticketStatuses.find(
      (status) => status.name === selectedStatusSort.value[0]
    );
    if (selectedStatus) {
      probeFilters.status_id = selectedStatus.id;
    }
  }

  try {
    const response = await postFetchAllTickets(probeFilters, { limit: 100 });
    const dates = (response?.data ?? [])
      .map((t) => t.scheduled_date?.split(/[T ]/)[0])
      .filter((d): d is string => !!d);
    if (dates.length === 0) return null;
    return dates.sort()[0];
  } catch (error) {
    console.error("Failed to probe next filtered ticket date:", error);
    return null;
  }
}

async function fetchPendingTickets() {
  const pendingStatus = store.ticketStatuses.find(
    (s) => s.name?.toLowerCase() === "pending",
  );
  if (!pendingStatus?.id) {
    pendingTicketsData.value = [];
    return;
  }
  isFetchingPendingTickets.value = true;
  try {
    const response = await fetchTickets({
      match_mode: TicketFiltersDTOMatchMode.and,
      status_id: pendingStatus.id,
    });
    pendingTicketsData.value = (response?.data ?? []) as FetchTicketResponse[];
  } finally {
    isFetchingPendingTickets.value = false;
  }
}

function isTicketIncomplete(ticket: FetchTicketResponse): boolean {
  return ticket.assignee == null || ticket.site == null || ticket.organisation == null
    || ticket.expected_duration == null || ticket.unit == null;
}

function sortByAttention(tickets: FetchTicketResponse[]): FetchTicketResponse[] {
  return tickets.sort((a, b) => {
    const aInc = isTicketIncomplete(a) ? 0 : 1;
    const bInc = isTicketIncomplete(b) ? 0 : 1;
    return aInc - bInc;
  });
}

function distributeTicketsToColumns(
  tickets: FetchTicketResponse[],
  dayColumns: Column<FetchTicketResponse>[]
): void {
  const hidden = store.hiddenStatusIds;
  const ticketsByDate = new Map<string, FetchTicketResponse[]>();
  for (const ticket of tickets) {
    if (ticket.status?.id && hidden.has(ticket.status.id)) continue;
    const dateKey = ticket.scheduled_date?.split(/[T ]/)[0];
    if (dateKey) {
      const list = ticketsByDate.get(dateKey) ?? [];
      list.push(ticket);
      ticketsByDate.set(dateKey, list);
    }
  }

  for (const column of dayColumns) {
    const dateKey = toLocalDateString(column.date);
    column.data = sortByAttention(ticketsByDate.get(dateKey) ?? []);
  }
}

function buildWeekSeparator(date: Date, weekNumber: number): Column<FetchTicketResponse> {
  return {
    id: `week-${weekNumber}-${faker.string.uuid()}`,
    title: `Week ${weekNumber}`,
    date,
    data: [],
    highlight: false,
    workload: null,
    workloadOverflow: null,
    isUnplanned: false,
    unassignedHours: 0,
    missingDurationCount: 0,
    mechanicBreakdown: [],
    weekNumber,
    separator: true,
  };
}

function buildDayColumn(date: Date, weekNumber: number): Column<FetchTicketResponse> {
  return {
    id: faker.string.uuid(),
    title: getDayName(date),
    date,
    data: [],
    highlight: isSameDay(date, new Date()),
    workload: null,
    workloadOverflow: null,
    isUnplanned: false,
    unassignedHours: 0,
    missingDurationCount: 0,
    mechanicBreakdown: [],
    weekNumber,
    separator: false,
  };
}

function buildGapSeparator(fromDate: Date, toDate: Date): Column<FetchTicketResponse> {
  return {
    id: `gap-${faker.string.uuid()}`,
    title: "Gap",
    date: toDate,
    gapFromDate: fromDate,
    data: [],
    highlight: false,
    workload: null,
    workloadOverflow: null,
    isUnplanned: false,
    unassignedHours: 0,
    missingDurationCount: 0,
    mechanicBreakdown: [],
    weekNumber: getISOWeek(toDate),
    separator: true,
    isGap: true,
  };
}

function appendDayBatch(startOfFetch: Date): { insertStart: number; dayColumns: Column<FetchTicketResponse>[] } {
  const tempColumns: Column<FetchTicketResponse>[] = [];
  let currentWeekNumber = getISOWeek(startOfFetch);

  const lastColumn = columns.value[columns.value.length - 1];
  const needsLeadingWeek = !lastColumn || getISOWeek(lastColumn.date) !== currentWeekNumber;
  if (needsLeadingWeek) {
    tempColumns.push(buildWeekSeparator(startOfFetch, currentWeekNumber));
  }

  for (let i = 0; i < daysPerScroll.value; i++) {
    const date = addDays(startOfFetch, i);
    const weekNumber = getISOWeek(date);
    if (weekNumber !== currentWeekNumber) {
      tempColumns.push(buildWeekSeparator(date, weekNumber));
      currentWeekNumber = weekNumber;
    }
    tempColumns.push(buildDayColumn(date, weekNumber));
  }

  const insertStart = columns.value.length;
  columns.value.push(...tempColumns);
  const dayColumns = columns.value.slice(insertStart).filter((c) => !c.separator);
  return { insertStart, dayColumns };
}

async function fetchAndDistributeBatch(
  dayColumns: Column<FetchTicketResponse>[],
  currentGeneration: number,
): Promise<{ cancelled: boolean; batchHasTickets: boolean }> {
  for (const col of dayColumns) loadingColumns.value.add(col.id);

  const startDateStr = toLocalDateString(dayColumns[0].date);
  const endDateStr = toLocalDateString(dayColumns[dayColumns.length - 1].date);
  const filters = buildTicketFilters(startDateStr, endDateStr);

  try {
    const response = await fetchTickets(filters);
    if (currentGeneration !== generationId) return { cancelled: true, batchHasTickets: false };
    distributeTicketsToColumns((response?.data || []) as unknown as FetchTicketResponse[], dayColumns);
    triggerRef(columns);
    return { cancelled: false, batchHasTickets: dayColumns.some((col) => col.data.length > 0) };
  } catch (error) {
    console.error("Failed to fetch tickets for columns:", error);
    for (const col of dayColumns) col.data = [];
    return { cancelled: false, batchHasTickets: false };
  } finally {
    for (const col of dayColumns) loadingColumns.value.delete(col.id);
  }
}

async function generateColumns() {
  if (isGenerating.value) return;
  if (noMoreFilteredResults.value) return;
  const currentGeneration = ++generationId;
  isGenerating.value = true;

  try {
    let startOfFetch = new Date(lastFetchedDate.value.getTime());
    if (columns.value.length > 0) {
      const lastColumn = columns.value[columns.value.length - 1];
      startOfFetch = addDays(lastColumn.date, 1);
    }

    let didJump = false;
    for (let attempt = 0; attempt < 2; attempt++) {
      const { insertStart, dayColumns } = appendDayBatch(startOfFetch);
      if (dayColumns.length === 0) break;

      const result = await fetchAndDistributeBatch(dayColumns, currentGeneration);
      if (result.cancelled) return;

      lastFetchedDate.value = addDays(startOfFetch, daysPerScroll.value);

      if (result.batchHasTickets || !hasJumpableFilter() || didJump) {
        if (hasActiveFilters.value && !result.batchHasTickets) {
          noMoreFilteredResults.value = true;
        }
        break;
      }

      const probeStart = toLocalDateString(addDays(dayColumns[dayColumns.length - 1].date, 1));
      const nextMatchDate = await probeNextFilteredTicketDate(probeStart);
      if (currentGeneration !== generationId) return;

      if (!nextMatchDate) {
        noMoreFilteredResults.value = true;
        break;
      }

      const jumpFromDate = dayColumns[0].date;
      const jumpToDate = parseISO(nextMatchDate);
      columns.value.splice(insertStart);
      columns.value.push(buildGapSeparator(jumpFromDate, jumpToDate));
      startOfFetch = jumpToDate;
      didJump = true;
    }

    updateWorkload();
  } finally {
    if (currentGeneration === generationId) {
      isGenerating.value = false;
    }
  }
}

async function refetchAllColumns() {
  const dayColumns = columns.value.filter((column) => !column.separator);
  if (dayColumns.length === 0) return;

  for (const col of dayColumns) {
    loadingColumns.value.add(col.id);
  }

  const startDateStr = toLocalDateString(dayColumns[0].date);
  const endDateStr = toLocalDateString(dayColumns[dayColumns.length - 1].date);
  const filters = buildTicketFilters(startDateStr, endDateStr);

  try {
    const response = await fetchTickets(filters);
    distributeTicketsToColumns((response?.data || []) as unknown as FetchTicketResponse[], dayColumns);

    const anyVisible = dayColumns.some((col) => col.data.length > 0);
    if (hasActiveFilters.value && !anyVisible) {
      noMoreFilteredResults.value = true;
    }
  } catch (error) {
    console.error("Failed to refetch tickets:", error);
    for (const col of dayColumns) {
      col.data = [];
    }
  } finally {
    for (const col of dayColumns) {
      loadingColumns.value.delete(col.id);
    }
  }

  updateWorkload();
}

async function refetchSingleColumn(dateKey: string) {
  const colBefore = columns.value.find(
    (col) => !col.separator && toLocalDateString(col.date) === dateKey,
  );
  if (!colBefore) return;

  loadingColumns.value.add(colBefore.id);
  const filters = buildTicketFilters(dateKey, dateKey);

  try {
    const response = await fetchTickets(filters);
    const colAfter = columns.value.find(
      (col) => !col.separator && toLocalDateString(col.date) === dateKey,
    );
    if (colAfter) {
      distributeTicketsToColumns((response?.data || []) as unknown as FetchTicketResponse[], [colAfter]);
      triggerRef(columns);
    }
  } catch (error) {
    console.error("Failed to refetch column:", error);
    const colAfter = columns.value.find(
      (col) => !col.separator && toLocalDateString(col.date) === dateKey,
    );
    if (colAfter) colAfter.data = [];
  } finally {
    if (colBefore) loadingColumns.value.delete(colBefore.id);
  }
}

function clearFilters() {
  store.clearFilters();
  noMoreFilteredResults.value = false;
  generationId++;
  isGenerating.value = false;
  columns.value = [];
  initializeStartDate();
  generateColumns();
  x.value = 0;
}

function onClickBoardItem(data: FetchTicketResponse) {
  selectedTicket.value = data;
  isTrelloBoardViewItemDialogVisible.value = true;
}

function handleSearchOpenTicket(ticket: FetchTicketResponse) {
  selectedTicket.value = ticket;
  isTrelloBoardViewItemDialogVisible.value = true;
}

function handleLocateOnBoard(ticket: FetchTicketResponse) {
  if (!ticket.scheduled_date) return;
  const targetDate = parseUTCDate(ticket.scheduled_date);
  searchInput.value = "";
  selectedStatusSort.value = [];
  selectedAssignees.value = [];
  selectedUnits.value = [];
  startDate.value = targetDate;
  handleDateChange();
}

function handleContextMenu(event: MouseEvent, ticket: FetchTicketResponse | null, column: Column<FetchTicketResponse>) {
  contextMenuTicket.value = ticket;
  contextMenuColumn.value = column;

  nextTick(() => {
    contextMenu.value?.showMenu(event);
  });
}

function handleTicketUpdate(ticket: FetchTicketResponse) {
  columns.value = columns.value.map((col) => ({
    ...col,
    data: col.data.map((t) => (t.id === ticket.id ? ticket : t)),
  }));
}

function removeTicketFromAllCollections(ticketId: string) {
  columns.value = columns.value.map((col) => ({
    ...col,
    data: col.data.filter((t) => t.id !== ticketId),
  }));
  unresolvedTicketsData.value = unresolvedTicketsData.value.filter((t) => t.id !== ticketId);
  unscheduledTicketsData.value = unscheduledTicketsData.value.filter((t) => t.id !== ticketId);
  pendingTicketsData.value = pendingTicketsData.value.filter((t) => t.id !== ticketId);
}

async function handleDialogTicketUpdate(updatedTicket: FetchTicketResponse) {
  if (!selectedTicket.value || !updatedTicket.id) return;

  const hidden = store.hiddenStatusIds;
  const originalDateKey = selectedTicket.value.scheduled_date?.split(/[T ]/)[0] ?? null;
  const newDateKey = updatedTicket.scheduled_date?.split(/[T ]/)[0] ?? null;
  const scheduledDateChanged = originalDateKey !== newDateKey;
  const isNowHidden = !!updatedTicket.status?.id && hidden.has(updatedTicket.status.id);

  if (!scheduledDateChanged && !isNowHidden) {
    if (hasActiveFilters.value && originalDateKey) {
      await refetchSingleColumn(originalDateKey);
    } else {
      columns.value = columns.value.map((col) => ({
        ...col,
        data: col.data.map((t) => (t.id === updatedTicket.id ? updatedTicket : t)),
      }));
      unresolvedTicketsData.value = unresolvedTicketsData.value.map((t) =>
        t.id === updatedTicket.id ? updatedTicket : t,
      );
      unscheduledTicketsData.value = unscheduledTicketsData.value.map((t) =>
        t.id === updatedTicket.id ? updatedTicket : t,
      );
      pendingTicketsData.value = pendingTicketsData.value.map((t) =>
        t.id === updatedTicket.id ? updatedTicket : t,
      );
    }
    await fetchPendingTickets();
    updateWorkload();
    return;
  }

  removeTicketFromAllCollections(updatedTicket.id);

  if (isNowHidden) {
    await fetchPendingTickets();
    updateWorkload();
    return;
  }

  const todayKey = toLocalDateString(new Date());

  if (newDateKey === null) {
    await fetchUnscheduledTickets({ params: { limit: 1000, page: 1 } });
  } else if (newDateKey < todayKey) {
    await fetchOverdueScheduledTickets();
  } else {
    await refetchSingleColumn(newDateKey);
  }

  await fetchPendingTickets();
  updateWorkload();
}

function handleUpdateTicketPlannedFor(ticket: FetchTicketResponse) {
  columns.value = columns.value.map((col) => ({
    ...col,
    data: col.data.filter((t) => t.id !== ticket.id),
  }));

  unscheduledTicketsData.value = unscheduledTicketsData.value.filter((t) => t.id !== ticket.id);
  unresolvedTicketsData.value = unresolvedTicketsData.value.filter((t) => t.id !== ticket.id);
  pendingTicketsData.value = pendingTicketsData.value.filter((t) => t.id !== ticket.id);

  const targetColumn = columns.value.find(
    (col) => !col.separator && toLocalDateString(col.date) === ticket.scheduled_date
  );

  if (targetColumn) {
    targetColumn.data.push(ticket);
    updateWorkload();
  } else if (ticket.scheduled_date) {
    unscheduledTicketsData.value.push(ticket);
  }
}

function handleTicketCut(ticket: FetchTicketResponse) {
  cutTicket.value = ticket;
  columns.value = columns.value.map((col) => ({
    ...col,
    data: col.data.filter((t) => t.id !== ticket.id),
  }));
}

function handleTicketPaste(ticket: FetchTicketResponse, column: Column<FetchTicketResponse>) {
  if (!cutTicket.value) return;

  const updatedTicket: FetchTicketResponse = {
    ...cutTicket.value,
    scheduled_date: toLocalDateString(column.date),
  };

  column.data.push(updatedTicket);
  cutTicket.value = null;
  updateWorkload();
}

async function handleCreateTicket(ticketId: string) {
  try {
    const ticketResponse = await fetchTicket(ticketId);
    selectedTicket.value = ticketResponse;
    isTrelloBoardViewItemDialogVisible.value = true;

    const dateKey = ticketResponse.scheduled_date?.split(/[T ]/)[0];
    if (dateKey) {
      await refetchSingleColumn(dateKey);
    }

    updateWorkload();
  } catch (error) {
    console.error(error);
  }
}

async function handleOpenTicket(ticketId: string) {
  const ticketResponse = await fetchTicket(ticketId);
  selectedTicket.value = ticketResponse;
  isTrelloBoardViewItemDialogVisible.value = true;
}

function handleDateChange() {
  noMoreFilteredResults.value = false;
  generationId++;
  isGenerating.value = false;
  columns.value = [];
  initializeStartDate();
  generateColumns();
}

function handleWorkloadBarClick(column: Column<FetchTicketResponse>) {
  if (pinnedWorkloadColumnId.value === column.id) {
    pinnedWorkloadColumnId.value = null;
  } else {
    pinnedWorkloadColumnId.value = column.id;
  }
}

function handleDraggingUpdate() {
  draggingTicket.value = true;
}

function handleDragEnd() {
  draggingTicket.value = false;
}


function handleEscapeKey(event: KeyboardEvent) {
  if (event.key === "Escape") {
    if (isSearchActive.value) {
      searchInput.value = "";
      return;
    }
    if (isUnresolvedExpanded.value && (isUnplannedExpanded.value || isPendingExpanded.value)) {
      isUnresolvedExpanded.value = false;
    } else if (isUnplannedExpanded.value && isPendingExpanded.value) {
      isUnplannedExpanded.value = false;
    } else if (!isUnresolvedExpanded.value && isUnplannedExpanded.value) {
      isUnplannedExpanded.value = false;
    } else if (isPendingExpanded.value) {
      isPendingExpanded.value = false;
    }
  }
}

const debouncedSearchRefetch = useDebounceFn(() => {
  if (columns.value.length > 0) {
    refetchAllColumns();
  }
}, 300);

watch(isTrelloBoardViewItemDialogVisible, (visible) => {
  if (!visible) selectedTicket.value = null;
}, { flush: 'post' });

watch(overdueScheduledTickets, (newVal) => {
  unresolvedTicketsData.value = newVal?.data ? newVal.data.map(t => ({ ...t } as FetchTicketResponse)) : [];
});

watch(unscheduledTickets, (newVal) => {
  unscheduledTicketsData.value = newVal?.data ? newVal.data.map(t => ({ ...t } as FetchTicketResponse)) : [];
});

watch([selectedAssignees, selectedStatusSort, selectedUnits], () => {
  noMoreFilteredResults.value = false;
  if (columns.value.length > 0) refetchAllColumns();
}, { deep: true });

watch(searchInput, () => {
  noMoreFilteredResults.value = false;
});

onMounted(async () => {
  searchInput.value = "";
  initializeStartDate();
  await store.fetchAllTicketStatuses();

  generateColumns();
  fetchOverdueScheduledTickets();
  fetchUnscheduledTickets({
    params: {
      limit: 1000,
      page: 1,
    },
  });
  fetchPendingTickets();
  window.addEventListener("keydown", handleEscapeKey);
  document.addEventListener("dragend", handleDragEnd);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleEscapeKey);
  document.removeEventListener("dragend", handleDragEnd);
});
</script>

<template>
  <div class="flex flex-col h-[calc(100vh-72px)] relative">
    <TrelloToolbar
      v-model:search-input="searchInput"
      v-model:selected-status-sort="selectedStatusSort"
      v-model:selected-priority-sort="selectedPrioritySort"
      v-model:selected-assignees="selectedAssignees"
      v-model:selected-units="selectedUnits"
      v-model:start-date="startDate"
      v-model:ticket-container-width="ticketContainerWidth"
      v-model:is-weekend-visible="isWeekendVisible"
      v-model:single-lane-scroll="singleLaneScroll"
      @clear-filters="clearFilters"
      @update:start-date="handleDateChange"
      @open-ticket="handleOpenTicket"
    />

    <div
      ref="infiniteScrollElement"
      v-infinite-scroll="[generateColumns, { distance: 1000, direction: 'right' }]"
      class="board-scroll-container overflow-x-scroll rounded relative flex-1"
      :class="{ 'overflow-y-scroll': !singleLaneScroll }"
    >
      <TrelloBoardSearchPanel
        v-if="isSearchActive"
        :filtered-unresolved="filteredUnresolved"
        :filtered-unplanned="filteredUnplanned"
        :filtered-pending="filteredPending"
        :filtered-scheduled="filteredScheduled"
        :is-searching="isSearching"
        :is-loading-more="isLoadingMore"
        :has-more-pages="hasMorePages"
        :search-error="searchError"
        @open-ticket="handleSearchOpenTicket"
        @locate-on-board="handleLocateOnBoard"
        @load-more="loadMoreResults"
        @close="searchInput = ''"
      />
      <div class="flex flex-row gap-3 w-max" :class="{ 'h-full': singleLaneScroll, 'min-h-full': !singleLaneScroll }">
      <div class="sticky left-0 top-0 z-40 flex backdrop-blur-sm" :style="{ height: `${boardHeight}px` }">
        <div class="flex flex-col gap-3 border-r border-gray-200 dark:border-gray-700 shadow-lg">
          <TrelloBoardColumnTab
            title="Unresolved"
            :count="unresolvedTicketsData.length"
            v-model:expanded="isUnresolvedExpanded"
          />

          <TrelloBoardColumnTab
            title="Unplanned"
            :count="unscheduledTicketsData.length"
            v-model:expanded="isUnplannedExpanded"
          />

          <TrelloBoardColumnTab
            title="Pending"
            :count="pendingTicketsData.length"
            v-model:expanded="isPendingExpanded"
          />
        </div>

        <div
          class="flex flex-row gap-3 bg-surface-ground shadow-lg border-r border-gray-200 dark:border-gray-700 h-full"
        >
          <TrelloColumn
            v-if="isUnresolvedExpanded"
            :column="{
              id: 'unresolved',
              title: 'Unresolved',
              date: null,
              data: unresolvedTicketsData,
              highlight: false,
              workload: null,
              workloadOverflow: null,
              isUnplanned: false,
              unassignedHours: 0,
              missingDurationCount: 0,
              mechanicBreakdown: [],
              weekNumber: 0,
            }"
            class="h-full overflow-y-auto z-50"
            :selected-status-sort="selectedStatusSort"
            :ticket-container-width="ticketContainerWidth"
            :dragging-ticket="draggingTicket"
            @click-item="onClickBoardItem"
            @context-menu="handleContextMenu"
            @workload-update="updateWorkload"
            @dragging-update="handleDraggingUpdate"
            :loading="isFetchingOverdueScheduledTickets"
            hide-workload
            hide-date
          />

          <TrelloColumn
            v-if="isUnplannedExpanded"
            :column="{
              id: 'unplanned',
              title: 'Unplanned',
              date: null,
              data: unscheduledTicketsData,
              highlight: false,
              workload: null,
              workloadOverflow: null,
              isUnplanned: false,
              unassignedHours: 0,
              missingDurationCount: 0,
              mechanicBreakdown: [],
              weekNumber: 0,
            }"
            class="h-full overflow-y-auto z-50"
            :selected-status-sort="selectedStatusSort"
            :ticket-container-width="ticketContainerWidth"
            :dragging-ticket="draggingTicket"
            @click-item="onClickBoardItem"
            @context-menu="handleContextMenu"
            @workload-update="updateWorkload"
            @dragging-update="handleDraggingUpdate"
            :loading="isFetchingUnscheduledTickets"
            hide-workload
            hide-date
          />

          <TrelloColumn
            v-if="isPendingExpanded"
            :column="{
              id: 'pending',
              title: 'Pending',
              date: null,
              data: pendingTicketsData,
              highlight: false,
              workload: null,
              workloadOverflow: null,
              isUnplanned: false,
              unassignedHours: 0,
              missingDurationCount: 0,
              mechanicBreakdown: [],
              weekNumber: 0,
            }"
            class="h-full overflow-y-auto z-50"
            :selected-status-sort="selectedStatusSort"
            :ticket-container-width="ticketContainerWidth"
            :dragging-ticket="draggingTicket"
            @click-item="onClickBoardItem"
            @context-menu="handleContextMenu"
            @workload-update="updateWorkload"
            @dragging-update="handleDraggingUpdate"
            :loading="isFetchingPendingTickets"
            hide-workload
            hide-date
          />
        </div>
      </div>

      <template v-for="(column, index) in visibleColumns" :key="index">
        <TrelloColumn
          v-if="!column.separator"
          :column="column"
          :selected-status-sort="selectedStatusSort"
          :ticket-container-width="ticketContainerWidth"
          :dragging-ticket="draggingTicket"
          :loading="loadingColumns.has(column.id)"
          :single-lane-scroll="singleLaneScroll"
          :show-workload-legend="column.id === firstDayColumnId"
          :show-workload-breakdown="pinnedWorkloadColumnId === column.id"
          @click-item="onClickBoardItem"
          @context-menu="handleContextMenu"
          @workload-update="updateWorkload"
          @workload-bar-click="handleWorkloadBarClick"
        />
        <div
          v-else-if="column.isGap"
          class="flex flex-col items-center justify-center px-3 py-2 rounded-lg border border-dashed border-amber-400 bg-amber-50 dark:bg-amber-900/40 dark:border-amber-600 shadow-md"
          data-testid="trello-board-gap-separator"
        >
          <div class="text-xs font-semibold text-amber-800 dark:text-amber-100 whitespace-nowrap">
            No matches
          </div>
          <div class="text-xs text-amber-700 dark:text-amber-200 whitespace-nowrap mt-1">
            {{ column.gapFromDate ? format(column.gapFromDate, "d MMM yyyy") : "" }}
            <span class="mx-1">→</span>
            {{ format(column.date, "d MMM yyyy") }}
          </div>
        </div>
        <!-- Separator weeks  -->
        <div v-else class="flex flex-col items-center justify-start px-2 py-2 rounded-lg shadow-md">
          <div class="flex-1 w-0.5 bg-gray-300 dark:bg-gray-600"></div>
          <div class="my-2 text-center whitespace-nowrap">
            <div class="font-semibold text-gray-600 dark:text-gray-200">
              Wk {{ column.weekNumber }}
            </div>
            <div class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {{ formatWeekRange(column.date) }}
            </div>
          </div>
          <div class="flex-1 w-0.5 bg-gray-300 dark:bg-gray-600"></div>
        </div>
      </template>
      </div>
    </div>
  </div>

  <TrelloContextMenu
    ref="contextMenu"
    :ticket="contextMenuTicket"
    :column="contextMenuColumn"
    :cut-ticket="cutTicket"
    @update:ticket="handleTicketUpdate"
    @update:ticket-planned-for="handleUpdateTicketPlannedFor"
    @cut="handleTicketCut"
    @paste="handleTicketPaste"
    @assign="isQuickAssignUserDrawerVisible = true"
    @open-ticket="handleOpenTicket"
    @create-ticket="handleCreateTicket"
  />

  <QuickAssignUserDrawer v-model="isQuickAssignUserDrawerVisible" />
  <TicketEditDialog
    v-model="isTrelloBoardViewItemDialogVisible"
    v-if="selectedTicket"
    :ticket-id="selectedTicket.id!"
    @update:ticket="handleDialogTicketUpdate"
  />
</template>

<style lang="scss" scoped>
.board-scroll-container {
  overscroll-behavior-x: none;
}

:deep(.column-header) {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--surface-card);
}

:deep(.layout-main) {
  width: 100% !important;
  max-width: none !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}
</style>
